import { JobCard } from "@/components/JobCard";
import { Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SkillBadge } from "@/components/SkillBadge";
import { JobData } from "@/hooks/jobs/useJobsData";
import { useTranslation } from "react-i18next";

interface JobOffersListProps {
  jobs: JobData[];
  viewMode: "card" | "list";
  isLoading?: boolean;
  refreshInterval?: number;
  enableAutoRefresh?: boolean;
}

export const JobOffersList = ({ jobs, viewMode, isLoading = false, refreshInterval, enableAutoRefresh }: JobOffersListProps) => {
  const { t } = useTranslation();

  if (jobs.length === 0) {
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow">
        <p className="text-gray-500">{t("jobs.noResults", "No job offers found matching your search criteria.")}</p>
      </div>
    );
  }

  if (viewMode === 'card') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {jobs.map(job => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {jobs.map(job => {
        const displaySkills = job.skills?.slice(0, 3) || [];
        const remainingSkills = job.skills?.length > 3 ? job.skills.length - 3 : 0;
        return (
          <div key={job.id} className="border rounded-lg p-3 bg-white hover:bg-gray-50 transition-colors">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium truncate">{job.title}</h4>
                  <p className="text-sm text-digger-blue truncate">{job.company.name}</p>
                  {displaySkills.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {displaySkills.map((skill) => (
                        <SkillBadge key={skill} label={skill} />
                      ))}
                      {remainingSkills > 0 && (
                        <SkillBadge 
                          label={`+${remainingSkills} more`} 
                          className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 md:mt-0">
                {job.matchCount !== undefined && (
                  <div className="text-sm whitespace-nowrap">
                    <span className="font-medium">{job.matchCount}</span> {t("jobs.potentialMatches", "potential matches")}
                  </div>
                )}
                <Link to={`/jobs/${job.id}`}>
                  <Button variant="outline" size="sm">{t("jobs.open", "Open")}</Button>
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
